// routes/_authenticated/fundos/$cnpj/route.tsx — pass-through layout so
// the fund page can have a child route at `/fundos/:cnpj/documentos` and
// a ticker alias at `/fundos/:ticker`. Renders only the child route's
// element; no chrome of its own.
//
// ponytail: the ticker→CNPJ redirect runs in `beforeLoad` so the URL
// bar shows the canonical CNPJ after the swap (replace:true) and the
// browser back-button doesn't trap the reader on the alias. It also
// runs for the child route (`/fundos/:ticker/documentos` rewrites to
// `/fundos/:cnpj/documentos`) because this layout file is the
// `$cnpj` segment's route — children inherit the same `beforeLoad`.
//
// Mirrors the `/companies/$cnpj/route.tsx` pattern, but the fund layout
// is even simpler: a single regex + a single search call, no ticker
// validity check needed beyond the shape.

import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";

import { cnpjForTicker, FUND_TICKER_RE } from "@/features/fundos/api";

export const Route = createFileRoute("/_authenticated/fundos/$cnpj")({
  // A non-CNPJ path segment (a B3 trading code like `HGLG11` or `BOVA11`)
  // is rewritten to the canonical CNPJ before the child route renders.
  // Unknown tickers fall through and the child renders the not-found
  // empty state — same behaviour as a typo'd CNPJ.
  beforeLoad: async ({ params, location }) => {
    const raw = decodeURIComponent(params.cnpj);
    if (!FUND_TICKER_RE.test(raw)) return;
    const cnpj = await cnpjForTicker(raw);
    if (!cnpj) return;
    // Swap only the segment, so `/fundos/HGLG11/documentos` keeps its
    // child path and search params.
    throw redirect({
      href: `${location.pathname.replace(/\/fundos\/[^/]+/, `/fundos/${cnpj}`)}${location.searchStr}`,
      replace: true,
    });
  },
  component: () => <Outlet />,
});
